import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useCurrentProfile, useOrganizationAlias } from "@domain/currentProfile/hooks";
import { PostComment } from "@domain/post/comment/postComment";
import { Person } from "@domain/user/user";
import { fullName } from "@domain/user/utils";
import { formatTimeAgo } from "@utils/date";
import { useAsync } from "@utils/hooks";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { useAuthModalBlocker } from "@ui/components/auth/useAuthModalBlocker";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { LikeIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { AuthLink } from "@ui/components/linkExternal";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { ProfilePicture, ProfilePictureSkeleton } from "@ui/components/profile/profilePicture";
import { TextSkeleton } from "@ui/components/skeleton/basicSkeletons";
import { ReactionsModal } from "@ui/components/post/reactionsModal";
import { CommentEditor } from "./commentEditor";

interface CommentViewProps extends React.HTMLAttributes<HTMLDivElement> {
	postComment: PostComment;
	postId: string;
	onEdit?: () => void;
	replyTo?: Person;
	onReply?: (to: Person) => void;
}
export const CommentView: React.FC<CommentViewProps> = ({
	postComment,
	postId,
	onEdit,
	replyTo,
	onReply,
	...htmlProps
}) => {
	const { id, author, text, lastUpdateDate, likes, parentId } = postComment;
	const { postService, trackingService } = useServices();
	const askDeleteConfirmation = useConfirmation(I18n.t("post.delete_comment.title"), I18n.t("delete"));
	const [editionMode, setEditionMode] = useState(false);
	const theme = useTheme();
	const currentProfile = useCurrentProfile();
	const alias = useOrganizationAlias();
	const isAuthor = alias ? alias.id === author.id : currentProfile?.id === author.id;
	const { createModal } = useModalTools();
	const { run: like, loading: liking } = useAsync(async () => {
		await postService.togglePostCommentLike(postComment);
		return trackingService.track("Feed - liked comment", {
			user: currentProfile?.primaryName,
			contentType: postComment.parentId,
		});
	}, [postComment]);
	// const { run: like, loading: liking } = useAsync(() => {
	// 	return postService.togglePostCommentLike(postComment);
	// }, [postComment]);

	const blockGuest = useAuthModalBlocker();

	if (editionMode) {
		return (
			<CommentEditor
				postId={postId}
				onCommentSubmit={() => setEditionMode(false)}
				onCancel={() => setEditionMode(false)}
				initialComment={postComment}
				parentId={parentId}
				replyTo={replyTo ?? undefined}
			/>
		);
	}

	return (
		<Container {...htmlProps} data-id={id}>
			<AuthLink to={author.type === "USER" ? Path.Profile(author) : Path.Organization(author)}>
				<Picture size={36} picture={author.avatar} fallbackType={author.type} />
			</AuthLink>
			<CommentWrapper>
				<Comment>
					<AuthLink to={author.type === "USER" ? Path.Profile(author) : Path.Organization(author)}>
						<Name>{fullName(author)}</Name>
						<Description>{author.description}</Description>
					</AuthLink>
					<Text content={text} fold backgroundColor={theme.colors.lightergray} hasInitialMention={!!parentId} />
					{likes.total > 0 && (
						<ActionInfo
							onClick={() =>
								blockGuest(() =>
									createModal((close) => (
										<ReactionsModal commentId={postComment.id} close={close} postId={postId} reaction="likes" />
									))
								)
							}
							colored={likes.byMe}
						>
							<LikeIcon />
							{likes.total}
						</ActionInfo>
					)}
				</Comment>
				{!!currentProfile && (
					<KebabMenu closeOnSelect>
						<SimpleMenuContent
							onDelete={
								isAuthor
									? () =>
											askDeleteConfirmation(async () => {
												await postService.deleteComment(postId, id, parentId);
											})
									: undefined
							}
							onEdit={isAuthor ? (parentId ? () => setEditionMode(true) : onEdit) : undefined}
							reportInfos={
								isAuthor
									? undefined
									: {
											contentId: id,
											contentType: "POST_COMMENT",
										}
							}
						/>
					</KebabMenu>
				)}
			</CommentWrapper>
			<DateAndActions>
				{!isAuthor && (
					<Action active={likes.byMe} onClick={() => blockGuest(() => !liking && like())}>
						{I18n.t("post.action.like")}
					</Action>
				)}
				<Action active={false} onClick={() => blockGuest(() => onReply?.(author))}>
					{I18n.t("post.action.reply")}
				</Action>
				<CommentDate>{formatTimeAgo(lastUpdateDate, true)}</CommentDate>
			</DateAndActions>
		</Container>
	);
};

export const PostCommentSkeleton: React.FC<React.HTMLAttributes<HTMLDivElement>> = (htmlProps) => (
	<Container {...htmlProps} style={{ gridTemplateColumns: "35px 150px" }}>
		<ProfilePictureSkeleton size={36} style={{ gridArea: "picture" }} />
		<Comment>
			<TextSkeleton style={{ marginBottom: 20 }} />
			<TextSkeleton />
		</Comment>
	</Container>
);

const Container = styled.div`
	display: grid;
	column-gap: 10px;
	row-gap: 5px;
	align-items: flex-end;
	grid-template-columns: 35px minmax(0, 100%);
	grid-template-areas:
		"picture comment"
		". date";
`;

const Picture = styled(ProfilePicture)`
	grid-area: picture;
`;

const CommentWrapper = styled.div`
	${row(5, "flex-start", "center")};
`;

const Comment = styled.div`
	max-width: 90%;
	position: relative;
	grid-area: comment;
	word-wrap: break-word;
	background-color: ${({ theme }) => theme.colors.lightergray};
	border-radius: 20px;
	border-bottom-left-radius: 0;
	padding: 14px 22px 12px;
	${({ theme }) => theme.textStyles.text};
`;

const Name = styled.div`
	${({ theme }) => theme.textStyles.secondaryFontFamily};
`;

const Text = styled(DraftRenderer)`
	margin-top: 7px;
	font-weight: 500;
`;

const ActionInfo = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "colored",
})<{ colored?: boolean }>`
	position: absolute;
	cursor: pointer;
	top: -10px;
	right: -12px;
	background-color: ${({ theme }) => theme.colors.white};
	box-shadow: 1px -1px 1px 1px rgba(0, 0, 0, 0.05);
	padding: 2px 3px;
	border-radius: 5px;
	${({ theme }) => theme.textStyles.label};
	${({ colored, theme }) => colored && `color: ${theme.colors.primary}`};
	display: flex;
	align-items: center;
	svg {
		margin-right: 4px;

		path {
			${({ colored, theme }) => colored && `fill: ${theme.colors.primary}`};
		}
	}
`;

const DateAndActions = styled.div`
	grid-area: date;
	${row(12)};
`;

const CommentDate = styled.span`
	${({ theme }) => theme.textStyles.tertiary};
`;

const Action = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>`
	cursor: pointer;
	${({ theme }) => theme.textStyles.label};

	&:hover {
		color: ${({ theme }) => theme.colors.gray};
	}

	${({ theme, active }) =>
		active &&
		css`
			color: ${theme.colors.primary};
			&:hover {
				color: ${theme.colors.primary};
			}
		`};
`;

const Description = styled.div`
	${({ theme }) => theme.textStyles.lighter};
	font-size: 0.625rem;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;
